import styled, { css, keyframes } from 'styled-components';
import { down } from 'styled-breakpoints';

import { TransientProps } from '@common/types';

export const PageWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    gap: 0;
    margin: -${theme.spacing[6]};
    min-height: calc(100vh - ${theme.layout.header});

    ${down('lg')} {
      margin: -${theme.spacing[4]};
    }

    ${down('md')} {
      margin: -${theme.spacing[3]};
    }
  `}
`;

export const MainContent = styled.main`
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  padding: 1.75rem 2rem;

  ${down('sm')} {
    padding: 1.25rem 1rem;
  }
`;

export const Sidebar = styled.aside`
  ${({ theme }) => css`
    width: 420px;
    flex-shrink: 0;
    border-left: 1px solid ${theme.colors.background[40]};
    background: ${theme.colors.background[100]};
    position: sticky;
    top: ${theme.layout.header};
    height: calc(100vh - ${theme.layout.header});
    overflow-y: auto;
    padding: 1.75rem 1.5rem;

    &::-webkit-scrollbar {
      width: 4px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background: ${theme.colors.background[40]};
      border-radius: 4px;
    }

    ${down('xl')} {
      display: none;
    }
  `}
`;

export const PageHeader = styled.header`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1.5rem;
    flex-wrap: wrap;
    gap: 0.75rem;

    h1 {
      font-size: 1.375rem;
      font-weight: 800;
      color: ${theme.colors.foreground[100]};
      letter-spacing: -0.5px;
    }
  `}
`;

export const PageHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

export const BackButton = styled.button`
  ${({ theme }) => css`
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 0.75rem;
    border: none;
    background: transparent;
    cursor: pointer;
    color: ${theme.colors.brand.primary.default};
    display: flex;
    align-items: center;
    justify-content: center;
    transition: ${theme.transition.default};

    :hover {
      background: ${theme.colors.brand.primary.opaque};
    }
  `}
`;

export const CountBadge = styled.span`
  ${({ theme }) => css`
    border-radius: 9999px;
    background: ${theme.colors.brand.primary.opaque};
    color: ${theme.colors.brand.primary.default};
    font-size: 0.75rem;
    font-weight: 700;
    padding: 0.25rem 0.75rem;
  `}
`;

export const FiltersRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  gap: 1rem;
  flex-wrap: wrap;
`;

export const FiltersLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.875rem;
  flex-wrap: wrap;
`;

export const FiltersRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.875rem;
  flex-wrap: wrap;
`;

export const DayTabs = styled.div`
  display: flex;
  gap: 0.375rem;
  margin-left: 2.5rem;
`;

export const DayTabWrapper = styled.div`
  position: relative;
`;

export const DayTabClose = styled.button`
  ${({ theme }) => css`
    position: absolute;
    top: -0.375rem;
    right: -0.375rem;
    width: 1.125rem;
    height: 1.125rem;
    border-radius: 50%;
    background: ${theme.colors.background[100]};
    border: 1px solid ${theme.colors.background[40]};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: ${theme.colors.foreground[60]};
    padding: 0;
    z-index: 2;
    transition: all 0.15s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

    &:hover {
      background: ${theme.colors.error[100]};
      color: #fff;
      border-color: ${theme.colors.error[100]};
    }
  `}
`;

type DayTabProps = TransientProps<{ active: boolean }>;

export const DayTab = styled.button<DayTabProps>`
  ${({ theme, $active }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0.5rem 1rem;
    border-radius: 0.75rem;
    min-width: 3.5rem;
    border: 1px solid
      ${$active ? theme.colors.foreground[100] : theme.colors.background[40]};
    background: ${$active
      ? theme.colors.foreground[100]
      : theme.colors.background[100]};
    color: ${$active ? '#fff' : theme.colors.foreground[60]};
    cursor: pointer;
    transition: ${theme.transition.default};

    > strong {
      font-size: 1.125rem;
      font-weight: 700;
      line-height: 1.1;
    }

    > span {
      font-size: 0.5625rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      opacity: ${$active ? 0.7 : 0.6};
    }

    :hover {
      border-color: ${theme.colors.brand.primary.default};
    }
  `}
`;

export const RoomDropdown = styled.div`
  ${({ theme }) => css`
    width: 16rem;

    .room-select-container {
      width: 100%;
    }

    .room-select__custom-value {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      position: absolute;
      left: 0.75rem;
    }

    .room-value-icon {
      color: ${theme.colors.foreground[40]};
      flex-shrink: 0;
    }

    .room-value-label {
      font-size: 0.8125rem;
      font-weight: 500;
      color: ${theme.colors.foreground[40]};
    }

    .room-value-name {
      font-size: 0.8125rem;
      font-weight: 700;
      color: ${theme.colors.foreground[100]};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 8rem;
    }

    .room-select__control {
      background: ${theme.colors.background[100]};
      border: 1px solid ${theme.colors.background[40]};
      border-radius: 0.5rem;
      min-height: 2.75rem;
      cursor: pointer;
      box-shadow: none;

      &:hover {
        border-color: ${theme.colors.brand.primary.default};
      }
    }

    .room-select__value-container {
      padding: 0 0.75rem;
    }

    .room-select__single-value {
      margin: 0;
    }

    .room-select__indicator-separator {
      display: none;
    }

    .room-select__dropdown-indicator {
      color: ${theme.colors.foreground[40]};
      padding: 0 0.75rem;
    }

    .room-select__menu {
      background: ${theme.colors.background[100]};
      border: 1px solid ${theme.colors.background[40]};
      border-radius: 0.5rem;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
      z-index: 20;
      overflow: hidden;
      margin-top: 0.25rem;
    }

    .room-select__menu-list {
      padding: 0.25rem 0;
    }

    .room-select__option {
      font-size: 0.8125rem;
      padding: 0.625rem 0.75rem;
      cursor: pointer;
      color: ${theme.colors.foreground[80]};
      background: transparent;
    }

    .room-select__option:hover {
      background: ${theme.colors.background[80]};
      color: ${theme.colors.brand.primary.default};
    }

    .room-select__option--is-selected {
      background: ${theme.colors.brand.primary.opaque};
      color: ${theme.colors.brand.primary.default};
      font-weight: 700;
    }

    .room-select__option--is-focused {
      background: ${theme.colors.background[80]};
    }
  `}
`;

export const DayIndicator = styled.div`
  ${({ theme }) => css`
    font-size: 0.8125rem;
    color: ${theme.colors.foreground[40]};
    margin-bottom: 1rem;
  `}
`;

export const SessionGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-left: 2.5rem;
`;

type SessionCardProps = TransientProps<{ color: string }>;

export const SessionCard = styled.div<SessionCardProps>`
  ${({ theme, $color }) => css`
    background: ${theme.colors.background[100]};
    border-radius: 1rem;
    border: 1px solid ${theme.colors.background[40]};
    overflow: hidden;
    position: relative;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    ::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 4px;
      background: ${$color};
      border-radius: 1rem 0 0 1rem;
    }

    :hover {
      transform: translateY(-3px);
      border-color: ${theme.colors.foreground[40]};
    }
  `}
`;

export const CardInner = styled.div`
  display: flex;
  gap: 1.125rem;
  padding: 1.25rem 1.375rem;
  align-items: flex-start;
`;

export const TimeBlock = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 3.5rem;
    padding-top: 0.125rem;

    > strong {
      font-size: 1.125rem;
      font-weight: 700;
      color: ${theme.colors.foreground[100]};
      line-height: 1.1;
    }

    > span {
      font-size: 0.625rem;
      color: ${theme.colors.foreground[40]};
      margin-top: 0.125rem;
    }
  `}
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

export const LiveChip = styled.div`
  ${({ theme }) => css`
    margin-top: 0.5rem;
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.1875rem 0.5rem;
    border-radius: 0.375rem;
    background: ${theme.colors.error[100]};
    color: #fff;
    font-size: 0.5rem;
    font-weight: 700;

    > span {
      width: 0.3125rem;
      height: 0.3125rem;
      border-radius: 50%;
      background: #fff;
      animation: ${pulse} 2s infinite;
    }
  `}
`;

export const CardContent = styled.div`
  flex: 1;
  min-width: 0;
`;

export const CardTitle = styled.div`
  ${({ theme }) => css`
    font-size: 0.9375rem;
    font-weight: 700;
    line-height: 1.3;
    margin-bottom: 0.25rem;
    color: ${theme.colors.foreground[100]};
  `}
`;

export const CardDesc = styled.p`
  ${({ theme }) => css`
    font-size: 0.75rem;
    color: ${theme.colors.foreground[60]};
    line-height: 1.5;
    margin-bottom: 0.75rem;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  `}
`;

export const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const RoomBadgeCard = styled.span`
  ${({ theme }) => css`
    position: absolute;
    top: 0.75rem;
    right: 0.75rem;
    display: inline-flex;
    align-items: center;
    gap: 0.3125rem;
    font-size: 0.6875rem;
    font-weight: 600;
    padding: 0.25rem 0.625rem;
    border-radius: 9999px;
    background: ${theme.colors.brand.primary.opaque};
    color: ${theme.colors.brand.primary.default};
  `}
`;

export const CardSpeakers = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 0.25rem;

    > span {
      font-size: 0.625rem;
      color: ${theme.colors.foreground[40]};
      margin-right: 0.25rem;
    }
  `}
`;

export const EmptyState = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3.75rem 0;
    grid-column: 1 / -1;

    > svg {
      color: ${theme.colors.foreground[40]};
      margin-bottom: 0.875rem;
    }

    > strong {
      font-size: 0.9375rem;
      font-weight: 600;
      color: ${theme.colors.foreground[60]};
      margin-bottom: 0.25rem;
    }

    > span {
      font-size: 0.75rem;
      color: ${theme.colors.foreground[40]};
    }
  `}
`;

export const SkeletonCard = styled.div`
  ${({ theme }) => css`
    background: ${theme.colors.background[100]};
    border-radius: 1rem;
    border: 1px solid ${theme.colors.background[40]};
    padding: 1.25rem 1.375rem;
    display: flex;
    gap: 1.125rem;
    align-items: flex-start;
  `}
`;

// ── Sidebar ─────────────────────────────────────────────

export const SidebarSectionTitle = styled.h3`
  ${({ theme }) => css`
    font-size: 0.875rem;
    font-weight: 700;
    color: ${theme.colors.foreground[100]};
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  `}
`;

export const LiveNowSection = styled.section`
  margin-bottom: 2rem;
`;

export const LiveBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  color: #ef4444;
`;

export const PulseDot = styled.span`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  background: #ef4444;
  animation: ${pulse} 1.5s ease-in-out infinite;
`;

export const LiveCard = styled.div`
  ${({ theme }) => css`
    background: ${theme.colors.background[80]};
    border-radius: 0.75rem;
    padding: 1rem;
    margin-top: 0.75rem;
  `}
`;

export const LiveCardTitle = styled.div`
  ${({ theme }) => css`
    font-size: 0.875rem;
    font-weight: 600;
    color: ${theme.colors.foreground[100]};
    margin-bottom: 0.25rem;
  `}
`;

export const LiveCardSpeaker = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;

  > img {
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 0.5rem;
    object-fit: cover;
  }

  > span {
    font-size: 0.75rem;
    color: var(--colors-foreground-60);
  }
`;

export const WatchButton = styled.button`
  ${({ theme }) => css`
    width: 100%;
    padding: 0.5rem;
    border: none;
    border-radius: 0.5rem;
    background: ${theme.colors.brand.primary.default};
    color: ${theme.colors.brand.primary.text};
    font-size: 0.75rem;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.9;
    }
  `}
`;

export const NextUpSection = styled.section`
  margin-bottom: 2rem;
`;

export const NextUpBadge = styled.span`
  ${({ theme }) => css`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.6875rem;
    font-weight: 700;
    text-transform: uppercase;
    color: ${theme.colors.brand.primary.default};
  `}
`;

export const NextUpCard = styled.div`
  ${({ theme }) => css`
    background: ${theme.colors.background[80]};
    border-radius: 0.75rem;
    padding: 1rem;
    margin-top: 0.75rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  `}
`;

export const NextUpTime = styled.div`
  ${({ theme }) => css`
    font-size: 0.6875rem;
    font-weight: 600;
    color: ${theme.colors.foreground[40]};
  `}
`;

export const NextUpTitle = styled.div`
  ${({ theme }) => css`
    font-size: 0.875rem;
    font-weight: 600;
    color: ${theme.colors.foreground[100]};
  `}
`;

export const NextUpSpeaker = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  > img {
    width: 2rem;
    height: 2rem;
    border-radius: 0.5rem;
    object-fit: cover;
  }

  > span {
    font-size: 0.75rem;
    color: var(--colors-foreground-60);
  }
`;

export const SpeakersSection = styled.section`
  margin-bottom: 1.5rem;
  position: relative;
`;

export const SpeakersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

export const SidebarEmptyState = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    min-height: 12rem;
    padding: 1.5rem 1rem;
    gap: 0.5rem;

    > svg {
      color: ${theme.colors.brand.primary.default};
      opacity: 0.85;
      margin-bottom: 0.25rem;
    }

    > strong {
      font-size: 0.875rem;
      font-weight: 600;
      color: ${theme.colors.foreground[100]};
    }

    > span {
      font-size: 0.75rem;
      color: ${theme.colors.foreground[60]};
      line-height: 1.4;
      max-width: 15rem;
    }
  `}
`;

export const SpeakersListFade = styled.div`
  ${({ theme }) => css`
    position: absolute;
    bottom: 1.5rem;
    left: 0;
    right: 0;
    height: 3rem;
    background: linear-gradient(transparent, ${theme.colors.background[100]});
    pointer-events: none;
  `}
`;

export const SpeakerItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.5rem;
  border-radius: 0.625rem;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    background: var(--colors-brand-primary-opaque);
  }
`;

export const SpeakerAvatar = styled.div`
  ${({ theme }) => css`
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 0.5rem;
    overflow: hidden;
    flex-shrink: 0;
    background: linear-gradient(
      135deg,
      ${theme.colors.brand.primary.opaque},
      ${theme.colors.brand.secondary.opaque}
    );

    > img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  `}
`;

export const SpeakerAvatarFallback = styled.div`
  ${({ theme }) => css`
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.875rem;
    font-weight: 700;
    color: ${theme.colors.brand.primary.default};
  `}
`;

export const SpeakerInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const SpeakerName = styled.div`
  ${({ theme }) => css`
    font-size: 0.8125rem;
    font-weight: 600;
    color: ${theme.colors.foreground[100]};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `}
`;

export const SpeakerTitle = styled.div`
  ${({ theme }) => css`
    font-size: 0.6875rem;
    color: ${theme.colors.foreground[40]};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `}
`;

export const ViewAllLink = styled.button`
  ${({ theme }) => css`
    display: block;
    width: 100%;
    text-align: center;
    font-size: 0.8125rem;
    font-weight: 600;
    color: ${theme.colors.brand.primary.default};
    background: none;
    border: none;
    cursor: pointer;
    margin-top: 0.5rem;
    padding: 0;
    font-size: 0.75rem;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.7;
    }
  `}
`;
